import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Check,
  X,
  Crown,
  Star,
  Heart,
  Shield,
  Briefcase,
  Store,
  Users,
  Sparkles,
  CreditCard,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Info,
  AlertTriangle,
  Award,
  BookOpen,
  Gift,
  Clock
} from 'lucide-react';
import { CharterCounter } from '../components/CharterCounter';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Membership Page - Visual comparison of all membership tiers
 * Important: FREE = Platform account (NOT a club member)
 *            PAID/PREMIUM = Club membership (member of a specific club)
 */
const Membership = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [charterPricing, setCharterPricing] = useState(null);
  const [featuredClub, setFeaturedClub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCharterPricing();
    fetchFeaturedClub();
  }, []);

  const fetchCharterPricing = async () => {
    try {
      const response = await fetch(`${API}/api/charter/pricing`);
      if (response.ok) {
        const data = await response.json();
        setCharterPricing(data);
      }
    } catch (error) {
      console.error('Error fetching charter pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedClub = async () => {
    try {
      const response = await fetch(`${API}/api/clubs/featured`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedClub(data);
      }
    } catch (error) {
      console.error('Error fetching featured club:', error);
    }
  };

  // Feature comparison data - Updated to reflect club membership model
  const features = [
    { name: 'View public content', free: true, paid: true, premium: true },
    { name: 'Create posts', free: '3/day', paid: 'Unlimited', premium: 'Unlimited' },
    { name: 'Post comments', free: '10/day', paid: 'Unlimited', premium: 'Unlimited' },
    { name: 'Add pets', free: '1 pet', paid: '5 pets', premium: 'Unlimited' },
    { name: 'Send messages', free: '5/day', paid: 'Unlimited', premium: 'Unlimited' },
    { name: 'Join events', free: '2/month', paid: 'Unlimited', premium: 'Unlimited' },
    { name: 'Club membership', free: false, paid: true, premium: true },
    { name: 'Club resources access', free: false, paid: true, premium: true },
    { name: 'Vote in elections', free: false, paid: true, premium: true },
    { name: 'Pet Friends feature', free: false, paid: true, premium: true },
    { name: 'Matchmaking', free: false, paid: true, premium: true },
    { name: 'Sell in Marketplace', free: false, paid: true, premium: true },
    { name: 'See Google Ads', free: true, paid: false, premium: false },
    { name: 'See Sponsor Ads', free: true, paid: true, premium: false },
    { name: 'Priority Support', free: false, paid: false, premium: true },
    { name: 'Early Feature Access', free: false, paid: false, premium: true },
  ];

  // Special member classes
  const specialClasses = [
    {
      name: 'Volunteer',
      icon: Heart,
      color: 'text-pink-600 bg-pink-100',
      description: 'Community heroes who help answer questions and moderate discussions',
      benefits: [
        'All PAID member benefits',
        '"First responder" for your pet expertise',
        'Special Volunteer badge',
        'Access to Volunteer channels',
        'No Google ads (sponsor ads only)'
      ],
      howToJoin: 'Express interest in forums or apply during volunteer calls'
    },
    {
      name: 'Breeder',
      icon: Star,
      color: 'text-amber-600 bg-amber-100',
      description: 'Verified breeders with registered breeding programs',
      benefits: [
        'Verified Breeder badge',
        'Litter management tools',
        'Health certification display',
        'Connect with puppy families',
        'Breed-specific forums access'
      ],
      howToJoin: 'Apply through Settings > Become a Breeder with documentation'
    },
    {
      name: 'Professional',
      icon: Briefcase,
      color: 'text-blue-600 bg-blue-100',
      description: 'Verified pet professionals (vets, trainers, groomers, behaviorists)',
      benefits: [
        'Professional credentials badge',
        'Business profile creation',
        'Professional Forums access',
        'Respond with credentials displayed',
        'Priority in service directory'
      ],
      howToJoin: 'Apply through Settings > Professional Verification'
    },
    {
      name: 'Vendor',
      icon: Store,
      color: 'text-purple-600 bg-purple-100',
      description: 'Business partners selling products or services',
      benefits: [
        'Marketplace storefront',
        'Business analytics dashboard',
        'Promotional tools',
        'Featured placement options',
        'Vendor badge on profile'
      ],
      howToJoin: 'Apply through Settings > Become a Vendor'
    }
  ];

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Check className="w-5 h-5 text-green-600" />;
    }
    if (value === false) {
      return <X className="w-5 h-5 text-muted-foreground/40" />;
    }
    return <span className="text-sm font-medium">{value}</span>;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="membership-page">
      <SEO 
        title="Membership"
        description="Join All American Dog Club. Choose from Free, Full Member ($5.99/mo), or Premium ($14.99/mo) tiers. Get access to health resources, community forums, local events, and more."
        url="/membership"
      />
      {/* Hero Section with Club Branding */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <Award className="w-8 h-8" />
          </div>
          <Badge className="bg-[#dc2626] text-white border-0 mb-4">
            {featuredClub?.name || 'All-American Dog Club'}
          </Badge>
          <h1 className="text-4xl font-heading font-medium mb-4">
            Membership Options
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-2">
            "{featuredClub?.tagline || 'Every Dog Is a Champion'}"
          </p>
          <p className="text-white/60 text-sm max-w-2xl mx-auto">
            Register for free platform access, or become a club member to unlock all benefits
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Important Note about Club Membership */}
        <Card className="max-w-4xl mx-auto mb-12 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium mb-2">Understanding Membership</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>FREE accounts</strong> give you access to the platform but do NOT make you a club member. 
                  You can browse, post (with limits), and explore the community.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>PAID and PREMIUM memberships</strong> include club membership to the{' '}
                  <span className="font-medium">{featuredClub?.name || 'All-American Dog Club'}</span>.
                  As a club member, you get access to exclusive resources, can vote in club elections, 
                  access the full marketplace, and enjoy all member benefits.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charter Discount Discount Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="overflow-hidden border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="beta-special-section">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  <span className="font-bold uppercase tracking-wide">Charter Discount - Limited Time!</span>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex items-center gap-2 text-amber-100 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Available during Founding Member preview only</span>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-amber-900 mb-2">
                  Get 2 Years for the Price of 1!
                </h3>
                <p className="text-amber-700">
                  Join now during our Founding Member preview and lock in this exclusive Charter deal.
                </p>
              </div>

              <CharterCounter className="mb-6" />
              <div className="grid md:grid-cols-2 gap-6">
                {/* Full Member Charter Discount */}
                <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-lg">Full Member</span>
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Charter Discount</Badge>
                  </div>
                  <div className="mb-4">
                    <span className="text-sm text-muted-foreground line-through">$119.98 for 2 years</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-amber-900">$59.99</span>
                      <span className="text-amber-700">for 2 full years</span>
                    </div>
                    <p className="text-xs text-green-600 font-medium mt-1">Save 50% - That's just $2.50/month!</p>
                  </div>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Full club membership benefits</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Ad-free experience</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Founding Member badge forever</span>
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-amber-500 hover:bg-amber-600"
                    onClick={() => navigate('/checkout?plan=paid_beta_2year')}
                    data-testid="beta-full-member-cta"
                  >
                    Get This Deal <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Premium Charter Discount */}
                <div className="bg-white rounded-xl p-5 border-2 border-amber-400 shadow-md relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-orange-500 text-white border-0">Best Value</Badge>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-orange-500" />
                    <span className="font-semibold text-lg">Premium Member</span>
                    <Badge className="bg-orange-100 text-orange-700 border-0 text-xs">Charter Discount</Badge>
                  </div>
                  <div className="mb-4">
                    <span className="text-sm text-muted-foreground line-through">$299.98 for 2 years</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-orange-900">$149.99</span>
                      <span className="text-orange-700">for 2 full years</span>
                    </div>
                    <p className="text-xs text-green-600 font-medium mt-1">Save 50% - That's just $6.25/month!</p>
                  </div>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Everything in Full Member</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Professional badge & forums</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Business listing & voting rights</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Founding Premium badge forever</span>
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                    onClick={() => navigate('/checkout?plan=premium_beta_2year')}
                    data-testid="beta-premium-member-cta"
                  >
                    Get Premium Deal <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tier Comparison */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl font-heading font-medium text-center mb-8">
            Compare Options
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* FREE Tier */}
            <Card className="relative">
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-gray-600" />
                </div>
                <CardTitle className="text-xl">FREE Account</CardTitle>
                <CardDescription>Platform access only</CardDescription>
                <Badge variant="outline" className="mt-2 text-xs">Not a club member</Badge>
                <div className="mt-4">
                  <span className="text-3xl font-bold">$0</span>
                  <span className="text-muted-foreground">/forever</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {features.slice(0, 8).map((feature) => (
                  <div key={`free-${feature.name}`} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{feature.name}</span>
                    {renderFeatureValue(feature.free)}
                  </div>
                ))}
                <div className="pt-4">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/register')}
                  >
                    Create Account
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* PAID Tier */}
            <Card className="relative border-[#1e3a5f] shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-[#1e3a5f]">Club Member</Badge>
              </div>
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <CardTitle className="text-xl">PAID Membership</CardTitle>
                <CardDescription>Full club member benefits</CardDescription>
                <Badge variant="secondary" className="mt-2 text-xs bg-green-100 text-green-700">
                  Includes club membership
                </Badge>
                <div className="mt-4">
                  <span className="text-3xl font-bold">$9.99</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                {charterPricing && (
                  <p className="text-xs text-green-600 mt-1">
                    Charter: ${(charterPricing.tiers[1]?.charter_price / 12).toFixed(2)}/mo
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {features.slice(0, 8).map((feature) => (
                  <div key={`paid-${feature.name}`} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{feature.name}</span>
                    {renderFeatureValue(feature.paid)}
                  </div>
                ))}
                <div className="pt-4">
                  <Button 
                    className="w-full"
                    style={{ backgroundColor: '#1e3a5f' }}
                    onClick={() => navigate('/settings')}
                  >
                    Join Club
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* PREMIUM Tier */}
            <Card className="relative border-amber-300">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-amber-500">Premium Member</Badge>
              </div>
              <CardHeader className="text-center pb-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl">PREMIUM Membership</CardTitle>
                <CardDescription>The ultimate experience</CardDescription>
                <Badge variant="secondary" className="mt-2 text-xs bg-green-100 text-green-700">
                  Includes club membership
                </Badge>
                <div className="mt-4">
                  <span className="text-3xl font-bold">$19.99</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {features.slice(0, 8).map((feature) => (
                  <div key={`premium-${feature.name}`} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{feature.name}</span>
                    {renderFeatureValue(feature.premium)}
                  </div>
                ))}
                <div className="pt-4">
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/settings/membership')}
                  >
                    Go Premium
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Full Feature Comparison Table */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-heading font-medium text-center mb-8">
            Full Feature Comparison
          </h2>
          
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-medium">Feature</th>
                    <th className="text-center p-4 font-medium">FREE</th>
                    <th className="text-center p-4 font-medium text-primary">PAID</th>
                    <th className="text-center p-4 font-medium text-amber-600">PREMIUM</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature) => (
                    <tr key={`table-${feature.name}`} className="border-b last:border-0">
                      <td className="p-4 text-sm">{feature.name}</td>
                      <td className="p-4 text-center">{renderFeatureValue(feature.free)}</td>
                      <td className="p-4 text-center bg-primary/5">{renderFeatureValue(feature.paid)}</td>
                      <td className="p-4 text-center">{renderFeatureValue(feature.premium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Ad Visibility Explanation */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-heading font-medium text-center mb-8">
            <Eye className="w-6 h-6 inline mr-2" />
            Ad Visibility by Tier
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <Eye className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="font-medium mb-2">FREE Members</h3>
              <p className="text-sm text-muted-foreground">
                See all ads including Google ads and sponsor/partner ads
              </p>
            </Card>
            
            <Card className="text-center p-6 border-primary">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-medium mb-2">PAID Members</h3>
              <p className="text-sm text-muted-foreground">
                Only see sponsor ads and special offers from trusted partners (no Google ads)
              </p>
            </Card>
            
            <Card className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <EyeOff className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="font-medium mb-2">PREMIUM Members</h3>
              <p className="text-sm text-muted-foreground">
                Completely ad-free experience throughout the platform
              </p>
            </Card>
          </div>
        </div>

        {/* Charter Membership */}
        {charterPricing && (
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-7 h-7 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-medium mb-2">
                      Charter Membership — Early-Member Pricing
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Founding Members and early supporters get Charter pricing for the duration of your subscription.
                      Lock in these introductory rates for up to 3 years.
                    </p>
                    
                    <div className="grid sm:grid-cols-4 gap-4 mb-4">
                      {charterPricing.tiers.map((tier) => (
                        <div key={tier.duration} className="bg-white rounded-lg p-4 text-center shadow-sm">
                          <p className="text-sm text-muted-foreground mb-1">{tier.months} months</p>
                          <p className="text-xl font-bold text-green-600">${tier.charter_price}</p>
                          <p className="text-xs text-muted-foreground line-through">${tier.regular_price}</p>
                          <p className="text-xs text-green-600">Save ${tier.savings}</p>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      No monthly option available. Commitment helps us plan for the community's future.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Special Member Classes */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl font-heading font-medium text-center mb-8">
            Special Member Classes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {specialClasses.map((memberClass) => {
              const Icon = memberClass.icon;
              return (
                <Card key={memberClass.name}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${memberClass.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{memberClass.name}</CardTitle>
                        <CardDescription>{memberClass.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-medium text-sm mb-2">Benefits:</h4>
                    <ul className="space-y-1 mb-4">
                      {memberClass.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground border-t pt-3">
                      <strong>How to join:</strong> {memberClass.howToJoin}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Privacy & Data Notice */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-medium mb-3">
                    Your Privacy Matters
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>
                      <strong>We do not share your data with anybody</strong> except what is required to:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Process payments securely through our payment provider (Stripe)</li>
                      <li>Fulfill essential business operations necessary to run the platform</li>
                    </ul>
                    <p>
                      <strong>About advertising:</strong> Ad links on our platform do not include any personally 
                      identifying data about you. However, once you click through to another website via ads 
                      or external links, we do not have control over how that website handles your information. 
                      We encourage you to review the privacy policies of any external sites you visit.
                    </p>
                    <p>
                      As a 501(c)(3) nonprofit organization, AADC will never sell your personal data 
                      or be sold to a commercial company. This is our core promise to the community.
                    </p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-primary"
                      onClick={() => navigate('/privacy')}
                    >
                      Read our full Privacy Policy →
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-heading font-medium mb-4">
            Ready to Join?
          </h2>
          <p className="text-muted-foreground mb-6">
            Start with a free account and upgrade anytime. Your support helps us build the best
            community for pet lovers everywhere.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/register')}
            >
              Create Free Account
            </Button>
            <Button 
              size="lg"
              className="btn-primary"
              onClick={() => navigate('/faq#membership')}
            >
              <Info className="w-4 h-4 mr-2" />
              Read FAQs
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
